import React, { useEffect, useState } from 'react';
import {
  Alert,
  SelectionControlLabel,
  Checkbox,
  Tooltip,
  IconButton
} from '@one-thd/sui-atomic-components';
import { InfoFilled } from '@one-thd/sui-icons';
import { GiftCardImagePlaceholder } from './gift-card-image-gallery';
import { GiftCardNickNameInput } from './gift-card-nickname-input';
import { GiftCardSelectImage } from './gift-card-select-image';
import { imgUrl } from '../../utils/consts';
import { useDrawer } from '../../hooks/use-drawer';
import { useGiftCardRequest } from '../../hooks/use-gift-card-request';
import { ISaveButtonProps } from '../../models/common-models';
import { setOverlayEvent, setButtonEvent } from '../../utils/analytics';
import { getImageName } from '../../utils/general';

interface IGCInfo {
  id: string;
  imagePath: string;
  nickName?: string;
  onlineTransactable: boolean;
  showActiveCheckbox: boolean;
}

interface IGiftCardEditProps {
  giftCardInfo: IGCInfo;
  onUpdated: (status?: string) => void;
}

export const GiftCardEdit: React.FC<IGiftCardEditProps> = ({ giftCardInfo, onUpdated }) => {
  const [showImageList, setShowImageList] = useState(false);
  const [editInfo, setEditInfo] = useState({ ...giftCardInfo });
  const [error, setError] = useState('');
  const { closeDrawer, updateDrawerState } = useDrawer();
  const { makeRequest: updateCard } = useGiftCardRequest({
    queryName: 'updateGiftCardV2'
  });
  const [updateError, setUpdateError] = useState('');

  useEffect(() => {
    const isGCEdited = (giftCardInfo?.nickName !== editInfo?.nickName && error === '')
      || giftCardInfo.imagePath !== editInfo.imagePath
      || giftCardInfo.onlineTransactable !== editInfo.onlineTransactable;
    updateSaveButtonProps({
      onClick: isGCEdited ? updateGC : undefined
    });
  }, [editInfo]);

  useEffect(() => {
    setOverlayEvent('edit');
  }, []);

  const updateSaveButtonProps = (saveProps: ISaveButtonProps) => {
    const newDrawerState = {
      saveChangesProps: saveProps,
      ContentComponent: GiftCardEdit
    };
    updateDrawerState(newDrawerState);
  };

  const handleError = (errorMessage: string) => {
    updateSaveButtonProps({
      loading: false,
      onClick: undefined
    });
    setUpdateError(errorMessage);
  };

  const handleNickNameError = (err: string) => {
    setError(err);
  };

  const updateGC = () => {
    updateSaveButtonProps({ loading: true });
    const variables = {
      giftCard: {
        id: editInfo.id,
        nickName: editInfo.nickName,
        imagePath: editInfo.imagePath,
        actions: {
          onlineTransactable: editInfo.onlineTransactable
        }
      }
    };

    updateCard(variables)
      .then((_data) => {
        if (_data?.paymentCards?.[0]?.saveToAccountStatus) {
          onUpdated();
          setButtonEvent(
            `cards and accounts: edited  gift card: ${
              giftCardInfo?.showActiveCheckbox
                ? `hd pass ${editInfo.onlineTransactable ? 'checked' : 'unchecked'}`
                : 'n/a'
            }`
          );
          closeDrawer();
        }
      })
      .catch((_err) => {
        const errorMessage = _err?.errorMessage;
        handleError(errorMessage);
      });
  };

  const handleImageSelect = (imageFileName: string) => {
    const imageFile = getImageName(imageFileName);
    const settings = `edit gift card overlay: change image success: image: ${imageFile}`;
    setButtonEvent(settings);
    setEditInfo({
      ...editInfo,
      imagePath: `${imgUrl.pathname}${imageFileName}`
    });
  };

  const onNicknameEdit = (nickName: string) => {
    setEditInfo((prevState) => {
      return {
        ...prevState,
        nickName
      };
    });
  };

  const updateOnlineTransactable = () => {
    setEditInfo({
      ...editInfo,
      onlineTransactable: !editInfo.onlineTransactable
    });
  };

  return (
    <div className="sui-flex sui-flex-col sui-gap-4">
      <GiftCardImagePlaceholder
        image={`${imgUrl.host}${editInfo?.imagePath}`}
        onClick={() => {
          setShowImageList(!showImageList);
          setOverlayEvent('edit', 'change image');
        }}
        buttonText={showImageList ? 'Close' : null}
      />
      {showImageList && (
        <GiftCardSelectImage
          userSelectedImg={editInfo.imagePath.replace(imgUrl.pathname, '')}
          onImageSelect={handleImageSelect}
        />
      )}
      <GiftCardNickNameInput
        gcNickName={editInfo?.nickName}
        onReady={onNicknameEdit}
        onError={handleNickNameError}
      />
      {giftCardInfo?.showActiveCheckbox && (
        <div className="sui-flex sui-items-center">
          <SelectionControlLabel
            className="!sui-mr-0"
            label="Enable this card in HD pass and online checkout."
          >
            <Checkbox
              checked={editInfo.onlineTransactable}
              onChange={updateOnlineTransactable}
              inputAttributes={{ 'aria-label': 'controlled' }}
            />
          </SelectionControlLabel>
          <Tooltip
            title="Check this box to make this gift card available at online checkout for purchases."
            data-testid="enable-gc-tooltip"
            placement="bottom-end"
          >
            <IconButton
              icon={InfoFilled}
              aria-label="Enable GC in checkout"
              iconSize="small"
              color="info"
              data-testid="enable-gc-tooltip-icon"
            />
          </Tooltip>
        </div>
      )}
      {updateError && <Alert status="error">{updateError}</Alert>}
    </div>
  );
};
